import React, { useState, useEffect, useCallback } from 'react';
import { getReceivedShares, getUnreadSharesCount, deleteShare } from '../services/api';
import { Share } from '../types/sharing';
import ConfirmDialog from './ConfirmDialog';
import { formatTimeAgo } from '../utils/dateFormatters';

interface SharedConversationsProps {
  onSelectShare: (shareId: string) => void;
  currentShareId?: string | null;
  onUnreadCountChange?: (count: number) => void;
  onError?: (message: string) => void;
  enablePolling?: boolean;
  pollingInterval?: number; // in milliseconds, default 30000 (30 seconds)
  refreshTrigger?: number; // Incrementing this triggers a refresh from parent
}

const MIN_POLLING_INTERVAL = 5000; // Minimum 5 seconds to prevent excessive API calls

const SharedConversations: React.FC<SharedConversationsProps> = ({
  onSelectShare,
  currentShareId,
  onUnreadCountChange,
  onError,
  enablePolling = false,
  pollingInterval = 30000,
  refreshTrigger = 0
}) => {
  const [shares, setShares] = useState<Share[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    shareId: string | null;
  }>({ isOpen: false, shareId: null });

  // Validate polling interval
  const validPollingInterval = Math.max(pollingInterval, MIN_POLLING_INTERVAL);

  const loadShares = useCallback(async () => {
    let isMounted = true;

    try {
      const response = await getReceivedShares();
      if (isMounted) {
        setShares(response.shares || []);
      }
    } catch (err: any) {
      console.error('Failed to load shares:', err);
      if (isMounted) {
        setError(err.message || 'Failed to load shared conversations');
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }

    return () => {
      isMounted = false;
    };
  }, []);

  const loadUnreadCount = useCallback(async () => {
    let isMounted = true;

    try {
      const response = await getUnreadSharesCount();
      const newCount = response.count || 0;

      if (isMounted) {
        setUnreadCount(newCount);

        // Notify parent component of count change
        if (onUnreadCountChange) {
          onUnreadCountChange(newCount);
        }
      }
    } catch (err) {
      console.error('Failed to load unread count:', err);
    }

    return () => {
      isMounted = false;
    };
  }, [onUnreadCountChange]);

  // Initial load
  useEffect(() => {
    loadShares();
    loadUnreadCount();
  }, [loadShares, loadUnreadCount]);

  // Polling for new shares
  useEffect(() => {
    if (!enablePolling) return;

    const interval = setInterval(() => {
      loadShares();
      loadUnreadCount();
    }, validPollingInterval);

    return () => clearInterval(interval);
  }, [enablePolling, validPollingInterval, loadShares, loadUnreadCount]);

  // Refresh when trigger changes (e.g., when a share is marked as viewed)
  useEffect(() => {
    if (refreshTrigger > 0) {
      console.log('[SharedConversations] Refresh triggered, reloading shares and unread count');
      loadShares();
      loadUnreadCount();
    }
  }, [refreshTrigger, loadShares, loadUnreadCount]);

  const handleSelectShare = async (share: Share) => {
    // Open the share viewer - marking as "viewed" will happen in ShareViewer
    // after the messages successfully load and user has time to view them
    onSelectShare(share.shareId);
  };

  const handleDeleteShare = (shareId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering onSelectShare

    if (isRefreshing) return; // Prevent deletion while refreshing

    // Open confirm dialog
    setConfirmDialog({ isOpen: true, shareId });
  };

  const confirmDelete = async () => {
    const shareId = confirmDialog.shareId;
    setConfirmDialog({ isOpen: false, shareId: null });

    if (!shareId) return;

    setIsRefreshing(true);
    try {
      await deleteShare(shareId);
      // Reload shares and unread count separately to avoid one failure breaking both
      loadShares().catch(err => console.error('Failed to reload shares:', err));
      loadUnreadCount().catch(err => console.error('Failed to reload unread count:', err));
    } catch (err: any) {
      console.error('Failed to delete share:', err);
      if (onError) {
        onError(err.message || 'Failed to delete share. Please try again.');
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const cancelDelete = () => {
    setConfirmDialog({ isOpen: false, shareId: null });
  };

  const truncateNote = (note: string | null, maxLength: number = 50): string => {
    if (!note) return 'No note';
    if (note.length <= maxLength) return note;
    return note.substring(0, maxLength) + '...';
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      borderTop: '1px solid var(--border-1)',
      paddingTop: '1rem',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 1rem 0.75rem',
    },
    title: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: 'var(--fg-1)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    badge: {
      backgroundColor: 'var(--danger)',
      color: 'white',
      borderRadius: '9999px',
      padding: '0.125rem 0.5rem',
      fontSize: '0.75rem',
      fontWeight: 600,
      minWidth: '1.25rem',
      textAlign: 'center',
    },
    sharesList: {
      flex: 1,
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      padding: '0 1rem',
    },
    shareItem: {
      padding: '0.75rem',
      borderRadius: 'var(--radius-md)',
      cursor: 'pointer',
      transition: 'all 0.2s',
      border: '1px solid transparent',
      position: 'relative',
    },
    shareItemUnread: {
      backgroundColor: 'var(--accent-soft)',
    },
    shareItemHover: {
      backgroundColor: 'var(--bg-hover)',
      borderColor: 'var(--border-1)',
    },
    shareItemActive: {
      backgroundColor: 'var(--accent-soft)',
      borderColor: 'var(--accent)',
    },
    shareHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '0.375rem',
    },
    fromUser: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: 'var(--fg-1)',
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    unreadDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: 'var(--accent)',
      flexShrink: 0,
    },
    shareNote: {
      fontSize: '0.8125rem',
      color: 'var(--fg-2)',
      marginBottom: '0.375rem',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    shareMeta: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: '0.75rem',
      color: 'var(--fg-3)',
    },
    messageCount: {
      flex: 1,
    },
    timestamp: {
      flexShrink: 0,
    },
    deleteButton: {
      position: 'absolute',
      top: '0.5rem',
      right: '0.5rem',
      background: 'rgba(239, 68, 68, 0.1)',
      border: 'none',
      borderRadius: 'var(--radius-sm)',
      color: 'var(--danger)',
      cursor: 'pointer',
      padding: '0.25rem 0.375rem',
      fontSize: '0.75rem',
      opacity: 0,
      transition: 'opacity 0.2s',
      fontWeight: 600,
    },
    emptyState: {
      padding: '2rem 1rem',
      textAlign: 'center',
      color: 'var(--fg-2)',
      fontSize: '0.875rem',
    },
    loadingState: {
      padding: '2rem 1rem',
      textAlign: 'center',
      color: 'var(--fg-2)',
      fontSize: '0.875rem',
    },
    errorState: {
      padding: '1rem',
      margin: '0 1rem',
      background: 'var(--danger-soft)',
      color: 'var(--danger)',
      borderRadius: 'var(--radius-md)',
      fontSize: '0.875rem',
    }
  };

  const [hoveredShareId, setHoveredShareId] = useState<string | null>(null);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.title}>Shared with Me</div>
        </div>
        <div style={styles.loadingState}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.title}>Shared with Me</div>
        </div>
        <div style={styles.errorState}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container} role="region" aria-label="Shared conversations" id="shared-conversations-panel">
      <div style={styles.header}>
        <div style={styles.title}>
          Shared with Me
          {unreadCount > 0 && (
            <span style={styles.badge} aria-label={`${unreadCount} unread shares`}>
              {unreadCount}
            </span>
          )}
        </div>
      </div>

      <div style={styles.sharesList} role="list">
        {shares.length === 0 ? (
          <div style={styles.emptyState} role="status">
            No shared conversations yet
          </div>
        ) : (
          shares.map(share => {
            // Validate share data
            const fromUserName = share.fromUserName || 'Unknown User';
            const messageCount = Array.isArray(share.messageIds) ? share.messageIds.length : 0;
            const isUnread = !share.viewedAt;

            return (
              <div
                key={share.shareId}
                role="listitem"
                tabIndex={0}
                aria-label={`Shared conversation from ${fromUserName}, ${messageCount} messages${isUnread ? ', unread' : ''}`}
                style={{
                  ...styles.shareItem,
                  ...(isUnread ? styles.shareItemUnread : {}),
                  ...(hoveredShareId === share.shareId ? styles.shareItemHover : {}),
                  ...(currentShareId === share.shareId ? styles.shareItemActive : {}),
                }}
                onClick={() => handleSelectShare(share)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelectShare(share);
                  }
                }}
                onMouseEnter={() => setHoveredShareId(share.shareId)}
                onMouseLeave={() => setHoveredShareId(null)}
              >
                <div style={styles.shareHeader}>
                  <div style={styles.fromUser}>
                    {fromUserName}
                  </div>
                  {isUnread && (
                    <div style={styles.unreadDot} aria-label="Unread" />
                  )}
                </div>

                <div style={styles.shareNote}>
                  "{truncateNote(share.notes)}"
                </div>

                <div style={styles.shareMeta}>
                  <span style={styles.messageCount}>
                    {messageCount} message{messageCount !== 1 ? 's' : ''}
                  </span>
                  <span style={styles.timestamp}>
                    {formatTimeAgo(share.sharedAt)}
                  </span>
                </div>

                {/* Delete button - shown on hover */}
                {hoveredShareId === share.shareId && (
                  <button
                    style={styles.deleteButton}
                    onClick={(e) => handleDeleteShare(share.shareId, e)}
                    aria-label={`Delete shared conversation from ${fromUserName}`}
                    title="Delete share"
                  >
                    ✕
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Delete Shared Conversation"
        message="Are you sure you want to delete this shared conversation? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default SharedConversations;
